import { useToolbarButtons } from "contexts/toolbars/hooks";
import { ToolbarButtonEntry, TooltipConfig } from "contexts/toolbars/reducer";
import { DefaultIconButton } from "features/quickAccessMenu/procore/defaultIconButton";

import { EnhancedTooltip } from "./tooltips";

interface ButtonRendererProps {
    toolbarId: string;
}

/**
 * Renders a single toolbar button entry (icon button + optional tooltip).
 * Returns null for separator entries and hidden buttons.
 */
export function ToolbarButton({ entry }: { entry: ToolbarButtonEntry }) {
    const { config, state } = entry;

    if (config.separator || !state.visible) {
        return null;
    }

    const ButtonComponent = config.component || DefaultIconButton;
    const tooltip: TooltipConfig | undefined =
        config.tooltip ??
        (config.title
            ? {
                  toolName: config.id,
                  label: config.title,
                  shortcutKey: "",
                  placement: "right",
              }
            : undefined);
    const buttonElement = (
        // Wrapping in a div to allow the tooltip to work when the
        // button is disabled.
        <div>
            <ButtonComponent
                id={config.id}
                onClick={config.onClick}
                active={state.active}
                disabled={state.disabled}
                title={tooltip ? undefined : config.title}
                ref={config.ref}
                hasExpander={config.hasExpander}
                expanderPosition={config.expanderPosition}
            >
                {config.icon}
            </ButtonComponent>
        </div>
    );

    if (tooltip) {
        return (
            <EnhancedTooltip config={tooltip} showOnHover={tooltip.showOnHover}>
                {buttonElement}
            </EnhancedTooltip>
        );
    }

    return buttonElement;
}

/**
 * Component that renders all buttons for a specific toolbar in the correct order
 */
export function ButtonRenderer({ toolbarId }: ButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);

    if (buttons.length === 0) {
        return null;
    }

    return (
        <>
            {buttons.map((buttonEntry) => (
                <ToolbarButton key={buttonEntry.config.id} entry={buttonEntry} />
            ))}
        </>
    );
}
